import { Link } from "react-router-dom";
import { ArrowRight, Check, Smartphone } from "lucide-react";
import { PageHero } from "../_components/page-hero";
import { PHASE_1, ROADMAP, PAGE_BREAKDOWN, UPCOMING } from "../_data/site";

export default function FeaturesPage() {
  return (
    <>
      <PageHero
        eyebrow="The full surface area"
        title={<>Bookings to settlement. <span className="gradient-text">One Business app.</span></>}
        sub="Live Business app features for single-owner vehicle rental businesses. Choose the modules you use; settings, reports and notifications stay available."
      >
        <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-primary" /> {PHASE_1.length} product capabilities</span>
          <span className="inline-flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" /> Web, Android &amp; iOS</span>
        </div>
      </PageHero>

      {/* Live modules */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-end justify-between border-b border-border pb-6">
            <div>
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">Shipping today</div>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">Business app features</h2>
            </div>
            <div className="hidden text-sm text-muted-foreground sm:block">Live product</div>
          </div>
          <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-y-0 sm:[&>*]:border-b sm:[&>*]:border-border lg:grid-cols-3">
            {PHASE_1.map((f) => (
              <div key={f.name} className="group flex items-start gap-4 px-2 py-6 transition-colors hover:bg-surface/40">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border border-border bg-surface text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display text-base font-semibold">{f.name}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Page breakdown — editorial cards, one per row */}
      <section className="border-t border-border bg-surface/30 py-20 sm:py-28">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="text-center">
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-primary">Inside the app</div>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight sm:text-4xl">A guided tour, screen by screen.</h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">Every page in RentOS is purpose-built. Here&apos;s what each one does, what you can do on it, and where it lives.</p>
          </div>

          <div className="mt-14 space-y-6">
            {PAGE_BREAKDOWN.map((p) => (
              <article key={p.key} className="rounded-2xl border border-border bg-background/40 p-6 transition-colors hover:bg-background/60 sm:p-8">
                <div className="flex items-start gap-5">
                  <div className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-gradient-primary text-primary-foreground">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h3 className="font-display text-xl font-semibold tracking-tight">{p.name}</h3>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{p.summary}</p>
                    <ul className="mt-5 grid gap-y-2 sm:grid-cols-2 sm:gap-x-6">
                      {p.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm">
                          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Where it runs */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-end justify-between border-b border-border pb-6">
            <div>
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-accent">One Business app</div>
              <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">At the counter or on your phone.</h2>
            </div>
            <div className="hidden text-sm text-muted-foreground sm:block">Available today</div>
          </div>
          <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-y-0 sm:[&>*]:border-b sm:[&>*]:border-border lg:grid-cols-3">
            {ROADMAP.map((f) => (
              <div key={f.name} className="flex items-start gap-4 px-2 py-6 opacity-90">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl border border-dashed border-border bg-surface/60 text-accent">
                  <f.icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-base font-semibold">{f.name}</h3>
                    <span className="rounded-full border border-border bg-surface px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Live</span>
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-primary px-6 py-3 text-sm font-medium text-primary-foreground shadow-glow"
            >
              See the Business app — book a demo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Upcoming integrations — clearly labelled as exploring, not live */}
      <section className="relative overflow-hidden border-t border-border py-20 sm:py-28">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-mesh opacity-40" />
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              On the horizon · exploring
            </div>
            <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              What we&rsquo;re <span className="gradient-text">building next.</span>
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              A glimpse at integrations we&rsquo;re actively exploring. These are not live yet — they are where RentOS is heading.
            </p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {UPCOMING.map((f) => (
              <div key={f.name} className="relative rounded-2xl border border-dashed border-border bg-surface/30 p-5">
                <span className="absolute right-4 top-4 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-accent">
                  Soon
                </span>
                <div className="grid h-10 w-10 place-items-center rounded-xl border border-border bg-background/60 text-accent">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold">{f.name}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            Want a say in what we build? <Link to="/contact" className="text-primary underline">Tell us what you need.</Link>
          </p>
        </div>
      </section>
    </>
  );
}
